import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, Mail, MapPin, ShieldCheck } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import Team from '@/components/Team';

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

const PRINCIPLES = [
  {
    title: 'Operators first',
    text: 'We run the brands ourselves. AI is leverage, not a substitute for ownership.',
  },
  {
    title: 'One stack, every brand',
    text: 'Tooling we build and tune in-house, applied across every brand in the portfolio.',
  },
  {
    title: 'Capacity over volume',
    text: 'We intentionally cap how many brands we hold and operate at once. Quality first.',
  },
  {
    title: 'Brand IP stays with the brand',
    text: 'For managed-service clients, AITOS operates — the brand entity owns its IP, customer data, and decisions.',
  },
];

function AboutPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>About AITOS Labs LLC — Operators first. AI second.</title>
        <meta
          name="description"
          content="AITOS Labs LLC is a POD operations house. We build and operate Print-on-Demand brands at scale. Flagship brand: Elzary."
        />
        <link rel="canonical" href="https://aitos.app/about" />
      </Helmet>

      <Navbar />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-purple-400 text-xs tracking-widest mb-4">ABOUT AITOS LABS LLC</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            We own, operate, and scale{' '}
            <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Print-on-Demand
            </span>{' '}
            brands.
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl mb-4">
            AITOS Labs LLC was founded in 2026 to build, own, and operate a portfolio of
            Print-on-Demand brands. Two brands are active in the portfolio today —{' '}
            <a
              href="/brands/elzary"
              className="text-purple-300 hover:text-purple-200 underline underline-offset-4"
            >
              Elzary
            </a>{' '}
            (POD apparel & lifestyle) and{' '}
            <a
              href="/brands/avyraatlas"
              className="text-pink-300 hover:text-pink-200 underline underline-offset-4"
            >
              Avyra Atlas
            </a>{' '}
            (Print-on-Demand lifestyle). Both run on the AITOS in-house storefront stack and an AI
            ops stack across design, listing, fulfillment, and customer experience.
          </p>
          <p className="text-gray-300 text-lg max-w-3xl">
            The same operating model is also available to outside POD sellers as a managed service.
            AITOS Labs LLC is a Wyoming limited liability company headquartered in Sheridan, WY.
          </p>
        </div>
      </section>

      {/* Principles */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-8">Founding principles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {PRINCIPLES.map((p) => (
              <div
                key={p.title}
                className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-purple-500/30 transition-colors"
              >
                <h3 className="text-xl font-bold mb-2">{p.title}</h3>
                <p className="text-sm text-gray-300">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Team />

      {/* Compliance */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto bg-gray-900 rounded-2xl p-10 border border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={24} className="text-purple-400" />
            <h2 className="text-2xl font-bold">Compliance & governance</h2>
          </div>
          <p className="text-gray-300 mb-4">
            AITOS Labs LLC complies with GDPR (EU) and CCPA (California, USA). Customer data is
            processed under DPA terms; sellers retain full ownership of brand and customer data.
            Cross-border data transfers use Standard Contractual Clauses where applicable.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-6 text-sm text-gray-300">
            <a
              href="mailto:hi@aitos.app"
              className="flex items-center gap-2 hover:text-purple-400 transition-colors"
            >
              <Mail size={18} />
              hi@aitos.app
            </a>
            <address className="not-italic flex items-start gap-2">
              <MapPin size={18} className="mt-0.5 shrink-0 text-gray-400" />
              <span>AITOS Labs LLC, 30 N Gould St Ste N, Sheridan, WY 82801, United States</span>
            </address>
          </div>
        </div>
      </section>

      {/* Dual CTA */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          <a
            href="/#cta?intent=investor"
            className="group bg-gradient-to-br from-purple-900/30 to-pink-900/20 rounded-2xl p-8 border border-purple-500/30 hover:border-purple-500/60 transition-all"
          >
            <p className="text-pink-300 text-xs tracking-widest uppercase mb-2">
              For investors / partners
            </p>
            <h3 className="text-2xl font-bold mb-2">Talk to the founders</h3>
            <p className="text-gray-300 mb-4">
              Operating model, portfolio plans, and roadmap on request.
            </p>
            <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
              Get in touch
              <ArrowRight size={16} />
            </span>
          </a>
          <a
            href="/#cta?intent=press"
            className="group bg-gray-900 rounded-2xl p-8 border border-gray-800 hover:border-purple-500/30 transition-all"
          >
            <p className="text-purple-300 text-xs tracking-widest uppercase mb-2">Press</p>
            <h3 className="text-2xl font-bold mb-2">Media inquiries</h3>
            <p className="text-gray-300 mb-4">Press kit and founder availability on request.</p>
            <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
              Email us
              <ArrowRight size={16} />
            </span>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
